import React, {
  useState,
  useEffect,
  useContext,
  memo,
  useCallback,
  useRef,
} from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Form,
  Button,
  Alert,
  Row,
  Col,
  ProgressBar,
  Spinner,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  IconUser,
  IconBuilding,
  IconChevronLeft,
  IconChevronRight,
  IconDeviceFloppy,
  IconRocket,
  IconSettings,
  IconPhoto,
  IconUpload,
  IconHelp,
  IconHeart,
} from "@tabler/icons-react";
import Select from "react-select";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

// Importar services
import {
  createAgent,
  updateAgent,
  prepareAgentData,
  listImages,
} from "../../../services/AgentsServices";
import UserContext from "../../../contexts/UserContext";
import NotificationsContext from "../../../contexts/NotificationsContext";
import {
  AREA_ACTIVITY_OPTIONS,
  GENDER_OPTIONS,
  PERSONALITY_OPTIONS,
} from "./AgentsConstants";

// Import mobile CSS
import "./agentWizardMobile.css";

const schemas = {
  step1: yup.object().shape({
    agentName: yup.string().required("Nome do agente é obrigatório"),
    agentJob: yup.string().required("Função do agente é obrigatório"),
    agentGender: yup.string().required("Gênero é obrigatório"),
    agentPersona: yup.array().min(1, "Selecione pelo menos uma personalidade"),
    agentArea: yup.string().required("Área de atuação é obrigatória"),
  }),
  step2: yup.object().shape({
    businessInfo: yup
      .string()
      .required("Informações sobre o negócio são obrigatórias")
      .max(1200, "Máximo de 1200 caracteres"),
  }),
};

const SimpleAgentWizard = memo(
  ({
    onUpgradeToAdvanced,
    isUpgrading = false,
    initialData = null,
    editData = null,
    isEditing = false,
    onBack = null,
  }) => {
    const navigate = useNavigate();
    const { user } = useContext(UserContext);
    const { setNotify } = useContext(NotificationsContext);

    const [currentStep, setCurrentStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [agentId, setAgentId] = useState(null);
    const [agentImage, setAgentImage] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [imageError, setImageError] = useState("");
    const [apiImageLibrary, setApiImageLibrary] = useState([]);
    const fileInputRef = useRef(null);
    const allowSubmitRef = useRef(false); // ✅ Ref para controlar quando submit é permitido

    // Função para mapear dados da API para o formulário simples
    const mapApiDataToSimpleForm = useCallback((apiData) => {
      if (!apiData) return {};

      return {
        agentName: apiData.agent_name || apiData.name || "",
        agentJob: apiData.agent_job || "",
        agentGender: apiData.agent_gender || "",
        agentPersona: apiData.agent_persona || [],
        agentArea: apiData.agent_area || "",
        businessInfo: apiData.business_info || "",
        agent_image: apiData.agent_image || null,
      };
    }, []);

    // Determinar dados iniciais (prioridade: editData > initialData)
    const getInitialData = () => {
      if (editData) {
        return mapApiDataToSimpleForm(editData);
      }
      if (initialData) {
        return initialData;
      }
      return {
        agentName: "",
        agentJob: "",
        agentGender: "",
        agentPersona: [],
        agentArea: "",
        businessInfo: "",
        agent_image: null,
      };
    };

    // Form setup
    const form = useForm({
      defaultValues: getInitialData(),
      mode: "onBlur", // ✅ FIX: Mudança para onBlur evita submit automático
      reValidateMode: "onBlur",
    });

    const {
      register,
      handleSubmit,
      formState: { errors },
      control,
      setValue,
      getValues,
      watch,
      reset,
    } = form;

    // Load initial data if provided (for upgrade scenario or editing)
    useEffect(() => {
      if (editData) {
        console.log("Carregando dados para edição (modo simples):", editData);
        const mappedData = mapApiDataToSimpleForm(editData);
        reset(mappedData);
        setAgentId(editData.id);
      } else if (initialData) {
        reset(initialData);
      }
    }, [editData, initialData, reset, mapApiDataToSimpleForm]);

    // Load image library from API
    const loadImageLibrary = useCallback(async () => {
      try {
        const response = await listImages();
        setApiImageLibrary(response.data.results);
      } catch (error) {
        console.error("Erro ao carregar biblioteca de imagens:", error);
      }
    }, []);

    // Load data on mount
    useEffect(() => {
      loadImageLibrary();
    }, [loadImageLibrary]);

    // Watch do valor agent_image do formulário
    const watchedAgentImage = watch("agent_image");

    // Sincronizar imagePreview com o valor do formulário
    useEffect(() => {
      console.log(
        "🖼️ [SimpleAgentWizard] agent_image do formulário mudou:",
        watchedAgentImage
      );

      // Reset de erros ao mudar imagem
      setImageError("");

      if (watchedAgentImage) {
        if (typeof watchedAgentImage === "string") {
          // É uma URL da API (modo edição)
          console.log(
            "🖼️ [SimpleAgentWizard] Definindo preview com URL da API:",
            watchedAgentImage
          );
          setImagePreview(watchedAgentImage);
          setAgentImage(null); // Não é um arquivo local
        } else if (watchedAgentImage instanceof File) {
          // É um arquivo local (upload novo)
          console.log(
            "🖼️ [SimpleAgentWizard] Definindo preview com arquivo local:",
            watchedAgentImage.name
          );

          // Evitar criar múltiplos FileReaders para o mesmo arquivo
          if (agentImage !== watchedAgentImage) {
            const reader = new FileReader();
            reader.onload = (e) => {
              console.log(
                "🖼️ [SimpleAgentWizard] FileReader concluído, definindo preview"
              );
              setImagePreview(e.target.result);
            };
            reader.onerror = (error) => {
              console.error(
                "❌ [SimpleAgentWizard] Erro ao ler arquivo:",
                error
              );
              setImageError("Erro ao carregar a imagem. Tente novamente.");
            };
            reader.readAsDataURL(watchedAgentImage);
            setAgentImage(watchedAgentImage);
          }
        } else {
          console.warn(
            "⚠️ [SimpleAgentWizard] Tipo de agent_image não reconhecido:",
            typeof watchedAgentImage,
            watchedAgentImage
          );
        }
      } else {
        // Não há imagem
        console.log("🖼️ [SimpleAgentWizard] Removendo preview - sem imagem");
        setImagePreview(null);
        setAgentImage(null);
      }
    }, [watchedAgentImage, agentImage]);

    const handleImageUpload = (event) => {
      const file = event.target.files[0];
      setImageError("");

      console.log(
        "📁 [SimpleAgentWizard] handleImageUpload chamado, arquivo:",
        file
      );

      if (!file) {
        console.log("❌ [SimpleAgentWizard] Nenhum arquivo selecionado");
        setValue("agent_image", null);
        return;
      }

      // Validar tipo de arquivo
      if (!file.type.startsWith("image/")) {
        console.log(
          "❌ [SimpleAgentWizard] Tipo de arquivo inválido:",
          file.type
        );
        setImageError("Por favor, selecione apenas arquivos de imagem.");
        // Reset do input para permitir nova seleção
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      // Validar tamanho máximo de 1MB
      if (file.size > 1024 * 1024) {
        console.log("❌ [SimpleAgentWizard] Arquivo muito grande:", file.size);
        setImageError("A imagem deve ter no máximo 1MB.");
        // Reset do input para permitir nova seleção
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
        return;
      }

      console.log(
        "✅ [SimpleAgentWizard] Arquivo válido, definindo no formulário"
      );
      // Definir o arquivo no formulário - o useEffect vai cuidar do preview
      setValue("agent_image", file);
    };

    const removeImage = () => {
      console.log("🗑️ [SimpleAgentWizard] Removendo imagem");
      setValue("agent_image", null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    };

    // Navigation functions
    const nextStep = async () => {
      console.log("🔍 [nextStep] Iniciando validação - Step:", currentStep);

      const currentSchema = schemas[`step${currentStep}`];
      if (currentSchema) {
        const isValid = await form.trigger();

        if (isValid) {
          const currentData = getValues();

          try {
            await currentSchema.validate(currentData, { abortEarly: false });
            console.log(
              "✅ [Mobile Fix] Validação do schema passou, avançando para próximo step"
            );
            console.log(
              "🔄 [STEP CHANGE] Mudando de step",
              currentStep,
              "para",
              Math.min(currentStep + 1, 2)
            );
            setCurrentStep((prev) => Math.min(prev + 1, 2));
          } catch (validationError) {
            console.error(
              "❌ [Mobile Fix] Erro de validação do schema:",
              validationError
            );
            console.error("❌ [Mobile Fix] Detalhes do erro:", {
              errors: validationError.errors,
              path: validationError.path,
              value: validationError.value,
            });
            Swal.fire({
              icon: "error",
              title: "Campos obrigatórios",
              text: `Por favor, preencha todos os campos obrigatórios: ${
                validationError.errors?.join(", ") ||
                "Verifique os campos marcados"
              }`,
            });
          }
        } else {
          console.error(
            "❌ [Mobile Fix] Trigger de validação falhou, errors:",
            form.formState.errors
          );
          console.error(
            "❌ [Mobile Fix] Dados atuais quando trigger falhou:",
            getValues()
          );

          // Mostrar erros específicos
          const errorMessages = Object.values(form.formState.errors)
            .map((error) => error.message)
            .filter(Boolean);

          Swal.fire({
            icon: "error",
            title: "Campos obrigatórios",
            text:
              errorMessages.length > 0
                ? `Por favor, corrija: ${errorMessages.join(", ")}`
                : "Por favor, preencha todos os campos obrigatórios antes de continuar.",
          });
        }
      } else {
        console.log(
          "🔍 [nextStep] Nenhum schema para validar, avançando diretamente"
        );
        setCurrentStep((prev) => Math.min(prev + 1, 2));
      }
    };

    const prevStep = () => {
      setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    // ✅ Função para validar step 2 e permitir submit
    const handleCreateAgent = async () => {
      console.log("🎯 [CREATE AGENT] Botão criar agente clicado");

      if (currentStep !== 2) {
        console.error("❌ [CREATE AGENT] Não está no step 2");
        return;
      }

      // Validar campos do step 2
      const step2Schema = schemas.step2;
      if (step2Schema) {
        const isValid = await form.trigger();

        if (isValid) {
          const currentData = getValues();
          console.log("🎯 [CREATE AGENT] Dados para validação:", currentData);

          try {
            await step2Schema.validate(currentData, { abortEarly: false });
            console.log(
              "✅ [CREATE AGENT] Validação passou, permitindo submit"
            );

            // Permitir submit e triggerar
            allowSubmitRef.current = true;

            // Triggerar submit programaticamente
            const form = document.getElementById("agent-form");
            if (form) {
              form.requestSubmit();
            }
          } catch (validationError) {
            console.error(
              "❌ [CREATE AGENT] Erro de validação:",
              validationError
            );
            Swal.fire({
              icon: "error",
              title: "Campos obrigatórios",
              text: `Por favor, preencha: ${
                validationError.errors?.join(", ") ||
                "Verifique os campos marcados"
              }`,
            });
          }
        } else {
          console.error(
            "❌ [CREATE AGENT] Trigger falhou:",
            form.formState.errors
          );
          const errorMessages = Object.values(form.formState.errors)
            .map((error) => error.message)
            .filter(Boolean);

          Swal.fire({
            icon: "error",
            title: "Campos obrigatórios",
            text:
              errorMessages.length > 0
                ? `Por favor, corrija: ${errorMessages.join(", ")}`
                : "Por favor, preencha todos os campos obrigatórios.",
          });
        }
      }
    };

    const onSubmit = async (data) => {
      console.log("🚨 [SUBMIT INTERCEPTED] onSubmit chamado!");
      console.log("🚨 [SUBMIT] Current Step:", currentStep);
      console.log("🚨 [SUBMIT] Allow Submit Ref:", allowSubmitRef.current);

      // Só permitir submit se a ref estiver true
      if (!allowSubmitRef.current) {
        console.error("❌ [SUBMIT] Bloqueado - allowSubmitRef é false");
        return;
      }

      // Reset da ref após usar
      allowSubmitRef.current = false;

      try {
        setLoading(true);

        console.log("🔍 Dados do formulário simples:", data);

        // ✅ NOVO: Separar agent_image para upload posterior
        const agentImageFile =
          data.agent_image instanceof File ? data.agent_image : null;

        // Preparar dados compatíveis com a API do AgentWizard
        const rawAgentData = {
          // Campos obrigatórios da API
          name: data.agentName,
          description: `${data.agentJob} ${data.agentPersona?.join(
            ", "
          )} para ${
            AREA_ACTIVITY_OPTIONS.find((opt) => opt.value === data.agentArea)
              ?.label || data.agentArea
          }`,
          agent_name: data.agentName,
          agent_job: data.agentJob,
          agent_gender: data.agentGender,
          agent_persona: data.agentPersona,
          agent_area: data.agentArea,
          business_info: data.businessInfo,

          // Campos com valores padrão para compatibilidade
          language: "default",
          language_style: ["friendly", "natural"],
          fundamental_rules: ["noMarkdown", "avoidFormal"],
          prohibited_words: "",
          preferred_words: "",
          address: "",
          payment_methods: [],

          // Fluxo básico padrão para agente informativo
          default_flow: {
            steps: [
              {
                id: "step_" + Date.now(),
                key: "greeting",
                label: "Saudação",
                icon: "👋",
                description:
                  "Como o agente vai cumprimentar cada pessoa que chegar",
                order: 0,
              },
            ],
            greeting: `Oi! Eu sou ${data.agentName}. Como posso te ajudar?`,
            greetingNextStep: "",
            customStepNames: {},
          },

          // Sem fluxos especiais
          special_flows: [],

          // Funções desabilitadas
          functions_enabled: false,
          function_responses: {},
          function_schemas: {},

          // Interações básicas
          interactions: {
            conectores: { enabled: false, value: "" },
            validacao: { enabled: false, value: "" },
            celebracao: { enabled: false, value: "" },
            upsell: { enabled: false, value: "" },
            emojis: { enabled: false, value: "" },
            mantra: { enabled: false, value: "" },
          },

          // Configurações da conta
          account: user.account_id,
          model: editData?.model || "gpt-4.1-mini",
          is_active: editData?.is_active || 0,

          // ✅ NOVOS CAMPOS
          simple_agent: true, // Marcar como agente simples
          is_published: editData?.is_published || false, // Manter status de publicação
        };

        // Se for string (URL), incluir no rawAgentData
        if (typeof data.agent_image === "string") {
          rawAgentData.agent_image = data.agent_image;
        }

        const agentDataWithImages = prepareAgentData(rawAgentData, []);

        console.log("🔍 Dados preparados para API:", agentDataWithImages);
        console.log("🔍 Arquivo de imagem para upload:", agentImageFile);

        let response;
        let createdAgentId;

        if (isEditing && agentId) {
          // ✅ EDITAR AGENTE EXISTENTE
          console.log("🔄 Atualizando agente simples:", agentId);
          createdAgentId = agentId;
          response = await updateAgent(agentId, agentDataWithImages);

          Swal.fire({
            icon: "success",
            title: "Agente atualizado com sucesso!",
            text: "As alterações foram salvas.",
            timer: 3000,
          });

          setNotify({
            type: "success",
            title: "Sucesso",
            message: "Agente básico atualizado com sucesso!",
          });
        } else {
          // ✅ CRIAR NOVO AGENTE
          response = await createAgent(agentDataWithImages);
          createdAgentId = response.data.id;

          if (!createdAgentId) {
            throw new Error("ID do agente não retornado pela API");
          }

          Swal.fire({
            icon: "success",
            title: "Agente criado com sucesso!",
            text: "Seu agente informativo está pronto para uso.",
            timer: 3000,
          });

          setNotify({
            type: "success",
            title: "Sucesso",
            message: "Agente básico criado com sucesso!",
          });
        }

        // ✅ SEGUNDA REQUISIÇÃO: Upload da imagem se existir
        if (agentImageFile) {
          console.log(
            "🔄 Fazendo upload da imagem do agente - Segunda requisição"
          );

          try {
            // Criar FormData apenas com a imagem
            const imageFormData = new FormData();
            imageFormData.append("agent_image", agentImageFile);

            // Fazer update do agente com a imagem
            const imageResponse = await updateAgent(
              createdAgentId,
              imageFormData
            );
            console.log("🔍 Resposta do upload da imagem:", imageResponse.data);
          } catch (imageError) {
            console.error("Erro ao fazer upload da imagem:", imageError);
            // Não falhar a criação do agente por erro na imagem
            Swal.fire({
              icon: "warning",
              title: "Agente criado, mas erro no upload da imagem",
              text: "O agente foi criado com sucesso, mas houve um erro ao fazer upload da imagem. Você pode editá-lo posteriormente para adicionar a imagem.",
              timer: 5000,
            });
          }
        }

        console.log("🔍 Resposta da API:", response.data);

        setTimeout(() => {
          navigate("/agents");
        }, 2500);
      } catch (error) {
        console.error("Erro ao criar agente:", error);

        let errorMessage =
          "Ocorreu um erro ao criar o agente. Tente novamente.";
        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }

        Swal.fire({
          icon: "error",
          title: "Erro ao criar agente",
          text: errorMessage,
        });

        if (setNotify) {
          setNotify({
            type: "error",
            title: "Erro",
            message: errorMessage,
          });
        }
      } finally {
        setLoading(false);
      }
    };

    // Step titles
    const steps = [
      { title: "Identidade", icon: IconUser },
      { title: "Sobre o Negócio", icon: IconBuilding },
    ];

    // Render step content
    const renderStepContent = () => {
      switch (currentStep) {
        case 1:
          return (
            <>
              <div className="agent-wizard-section-header d-md-none">
                <div className="agent-wizard-section-icon">
                  <IconUser size={20} />
                </div>
                <h4 className="agent-wizard-section-title">
                  Identidade do Agente
                </h4>
              </div>

              <div className="d-none d-md-flex align-items-center mb-4">
                <IconUser className="me-2 text-primary" size={24} />
                <h4 className="mb-0">Identidade do Agente</h4>
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      Defina quem é seu agente e como ele se comporta
                    </Tooltip>
                  }
                >
                  <IconHelp
                    className="ms-2 text-muted"
                    size={16}
                    style={{ cursor: "help" }}
                  />
                </OverlayTrigger>
              </div>

              {/* Campo de Upload da Foto do Perfil */}
              <Row className="mb-4">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>
                      <IconPhoto className="me-2" size={16} />
                      Foto do Perfil
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Adicione uma foto para o perfil do agente (máximo
                            1MB)
                          </Tooltip>
                        }
                      >
                        <IconHelp
                          className="ms-1 text-muted"
                          size={14}
                          style={{ cursor: "help" }}
                        />
                      </OverlayTrigger>
                    </Form.Label>

                    <div className="d-flex align-items-start gap-3">
                      {/* Preview da imagem ou placeholder */}
                      <div
                        className="border rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: "100px",
                          height: "100px",
                          backgroundColor: "#f8f9fa",
                          overflow: "hidden",
                        }}
                      >
                        {imagePreview ? (
                          <img
                            src={imagePreview}
                            alt="Preview do agente"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <IconUser size={40} className="text-muted" />
                        )}
                      </div>

                      {/* Controles de upload */}
                      <div className="flex-grow-1">
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          style={{ display: "none" }}
                        />

                        <div className="d-flex gap-2 mb-2">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <IconUpload className="me-1" size={16} />
                            {imagePreview ? "Alterar Foto" : "Selecionar Foto"}
                          </Button>

                          {imagePreview && (
                            <Button
                              variant="outline-danger"
                              size="sm"
                              onClick={removeImage}
                            >
                              Remover
                            </Button>
                          )}
                        </div>

                        <small className="text-muted">
                          Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 1MB
                        </small>

                        {imageError && (
                          <Alert variant="danger" className="mt-2 mb-0 py-2">
                            {imageError}
                          </Alert>
                        )}
                      </div>
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nome do Agente *</Form.Label>
                    <Form.Control
                      {...register("agentName")}
                      placeholder="Ex: Maria Atendimento"
                      isInvalid={!!errors.agentName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.agentName?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Função</Form.Label>
                    <Form.Control
                      {...register("agentJob")}
                      placeholder="Ex: secretária, atendente virtual..."
                      isInvalid={!!errors.agentJob}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.agentJob?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Gênero *</Form.Label>
                    <Controller
                      name="agentGender"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={GENDER_OPTIONS}
                          placeholder="Selecione o gênero..."
                          value={GENDER_OPTIONS.find(
                            (opt) => opt.value === field.value
                          )}
                          onChange={(selected) =>
                            field.onChange(selected?.value || "")
                          }
                          className={errors.agentGender ? "is-invalid" : ""}
                        />
                      )}
                    />
                    {errors.agentGender && (
                      <div className="invalid-feedback d-block">
                        {errors.agentGender?.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Área de Atuação *</Form.Label>
                    <Controller
                      name="agentArea"
                      control={control}
                      render={({ field }) => (
                        <Select
                          {...field}
                          options={AREA_ACTIVITY_OPTIONS}
                          placeholder="Selecione a área..."
                          isSearchable
                          value={AREA_ACTIVITY_OPTIONS.find(
                            (opt) => opt.value === field.value
                          )}
                          onChange={(selected) =>
                            field.onChange(selected?.value || "")
                          }
                          className={errors.agentArea ? "is-invalid" : ""}
                        />
                      )}
                    />
                    {errors.agentArea && (
                      <div className="invalid-feedback d-block">
                        {errors.agentArea?.message}
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label className="py-2">
                  <IconHeart size={18} />
                  Personalidade do Agente *
                </Form.Label>

                {/* Desktop Layout */}
                <div className="d-none d-md-flex flex-wrap gap-2">
                  {PERSONALITY_OPTIONS.map((personality) => (
                    <Form.Check
                      key={personality}
                      type="checkbox"
                      id={`personality-${personality}`}
                      label={personality}
                      value={personality}
                      {...register("agentPersona")}
                      className="me-3"
                    />
                  ))}
                </div>

                {/* Mobile Layout */}
                <div className="agent-wizard-checkbox-group d-md-none">
                  {PERSONALITY_OPTIONS.map((personality) => (
                    <label
                      key={personality}
                      className="agent-wizard-checkbox-item"
                      htmlFor={`mobile-personality-${personality}`}
                    >
                      <input
                        type="checkbox"
                        id={`mobile-personality-${personality}`}
                        value={personality}
                        {...register("agentPersona")}
                        onChange={(e) => {
                          console.log(
                            "📱 [Mobile Checkbox] Mudança detectada:",
                            {
                              personality,
                              checked: e.target.checked,
                              currentValues: getValues("agentPersona"),
                            }
                          );

                          // Chamar o onChange original do register
                          const registerProps = register("agentPersona");
                          if (registerProps.onChange) {
                            registerProps.onChange(e);
                          }
                        }}
                      />
                      <span className="agent-wizard-checkbox-label">
                        {personality}
                      </span>
                    </label>
                  ))}
                </div>

                {errors.agentPersona && (
                  <div className="text-danger small mt-2">
                    {errors.agentPersona.message}
                  </div>
                )}
              </Form.Group>
            </>
          );

        case 2:
          return (
            <>
              <div className="agent-wizard-section-header d-md-none">
                <div className="agent-wizard-section-icon">
                  <IconBuilding size={20} />
                </div>
                <h4 className="agent-wizard-section-title">Sobre o Negócio</h4>
              </div>

              <div className="d-none d-md-flex align-items-center mb-4">
                <IconBuilding className="me-2 text-primary" size={24} />
                <h4 className="mb-0">Sobre o Negócio</h4>
              </div>

              <Form.Group className="mb-4">
                <Form.Label>
                  Informações sobre o Negócio * (máximo 1200 caracteres)
                </Form.Label>
                <Controller
                  name="businessInfo"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Form.Control
                      {...field}
                      as="textarea"
                      rows={10}
                      placeholder="Descreva o que sua empresa faz, como funciona o processo, principais benefícios, diferenciais, horários, tipos de atendimento, formas de pagamento, etc."
                      maxLength={1200}
                      isInvalid={!!errors.businessInfo}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.businessInfo?.message}
                </Form.Control.Feedback>
                <Form.Text className="text-muted">
                  {watch("businessInfo")?.length || 0}/1200 caracteres
                </Form.Text>
              </Form.Group>

              <Alert variant="info">
                <h6>Como seu agente funcionará:</h6>
                <ul className="mb-0">
                  <li>Cumprimentará os clientes de forma amigável</li>
                  <li>Responderá perguntas usando as informações fornecidas</li>
                  <li>Oferecerá ajuda adicional quando necessário</li>
                  <li>
                    Manterá um tom{" "}
                    {watch("agentPersona")?.join(", ").toLowerCase() ||
                      "profissional"}
                  </li>
                </ul>
              </Alert>

              {!isUpgrading && onUpgradeToAdvanced && (
                <Alert variant="warning">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Precisa de mais recursos?</strong>
                      <p className="mb-0 mt-1">
                        Fluxos personalizados, funções avançadas e integrações.
                      </p>
                    </div>
                    <Button
                      variant="outline-warning"
                      size="sm"
                      type="button"
                      onClick={() => onUpgradeToAdvanced(getValues())}
                    >
                      <IconSettings className="me-1" size={16} />
                      Usar Avançado
                    </Button>
                  </div>
                </Alert>
              )}
            </>
          );

        default:
          return null;
      }
    };

    return (
      <>
        {/* Mobile Header */}
        <div className="agent-wizard-header d-md-none">
          <div className="agent-wizard-header-content">
            {onBack && (
              <Button
                className="agent-wizard-back-btn"
                type="button"
                onClick={onBack}
              >
                <IconChevronLeft size={16} />
                Voltar
              </Button>
            )}
            <h1 className="agent-wizard-title">
              {isEditing ? "Editar Agente" : "Criar Agente"}
            </h1>
            <div style={{ width: "80px" }}></div>
          </div>
        </div>

        {/* Mobile Progress */}
        <div className="agent-wizard-progress-container d-md-none">
          <div className="agent-wizard-progress-bar">
            <div
              className="agent-wizard-progress-fill"
              style={{ width: `${(currentStep / 2) * 100}%` }}
            ></div>
          </div>
          <div className="agent-wizard-step-info">
            <span className="agent-wizard-step-title">
              {steps[currentStep - 1]?.title}
            </span>
            <span>Etapa {currentStep} de 2</span>
          </div>
        </div>

        {/* Main Container */}
        <div className="card no-hover pb-4">
          <div className="agent-wizard-mobile-container d-md-none">
            <div className="agent-wizard-content">
              {/* Mobile Alerts */}
              {isEditing ? (
                <Alert variant="info" className="d-md-none">
                  <strong>Editando agente básico</strong>
                  <br />
                  Suas alterações serão salvas ao finalizar.
                </Alert>
              ) : (
                <Alert variant="info" className="d-md-none">
                  <strong>Agente Básico</strong>
                  <br />
                  Crie rapidamente um agente para responder dúvidas sobre seu
                  negócio.
                </Alert>
              )}

              {/* Mobile Form */}
              <Form
                onSubmit={(e) => {
                  // Só permitir submit no step 2
                  if (currentStep !== 2) {
                    e.preventDefault();
                    return;
                  }

                  handleSubmit(onSubmit)(e);
                }}
                id="agent-form"
              >
                <div className="agent-wizard-form-content">
                  <div className="agent-wizard-form-section">
                    {renderStepContent()}
                  </div>
                </div>
              </Form>
            </div>
          </div>

          {/* Unified Form for Desktop */}
          <Form
            onSubmit={(e) => {
              // Só permitir submit no step 2
              if (currentStep !== 2) {
                e.preventDefault();
                return;
              }

              handleSubmit(onSubmit)(e);
            }}
            id="agent-form-desktop"
          >
            {/* Desktop Layout */}
            <Row className="d-none d-md-flex">
              <Col lg={12} sm={12} className="mx-auto">
                <div className="p-4">
                  <Row>
                    <Col lg={12}>
                      {/* Desktop Header */}
                      <div className="d-flex align-items-center justify-content-between mb-4">
                        <div className="d-flex align-items-center">
                          {onBack && (
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="me-3"
                              type="button"
                              onClick={onBack}
                            >
                              <IconChevronLeft className="me-1" size={16} />
                              Voltar
                            </Button>
                          )}
                          <h3 className="mb-0">
                            <IconRocket className="me-2" />
                            {isEditing
                              ? "Editar Agente Básico"
                              : "Criar Agente Básico"}
                          </h3>
                        </div>
                      </div>

                      {isEditing ? (
                        <Alert variant="info" className="mb-4">
                          <strong>Modo de Edição:</strong> Você está editando um
                          agente básico. Todas as alterações serão salvas ao
                          finalizar.
                        </Alert>
                      ) : (
                        <Alert variant="info" className="mb-4">
                          <strong>Modo Simplificado:</strong> Crie rapidamente
                          um agente informativo que responde dúvidas sobre seu
                          negócio. Perfeito para começar! 😊
                        </Alert>
                      )}

                      {/* Progress Bar */}
                      <ProgressBar
                        now={(currentStep / 2) * 100}
                        className="mb-4"
                        style={{ height: "8px" }}
                      />

                      {/* Steps Navigation */}
                      <div
                        className="d-flex justify-content-center mb-4"
                        style={{ flexWrap: "wrap", gap: "20px" }}
                      >
                        {steps.map((step, index) => {
                          const StepIcon = step.icon;
                          const stepNumber = index + 1;
                          const isActive = currentStep === stepNumber;
                          const isCompleted = currentStep > stepNumber;

                          return (
                            <div
                              key={stepNumber}
                              className={`d-flex flex-column align-items-center ${
                                isActive
                                  ? "text-primary"
                                  : isCompleted
                                  ? "text-success"
                                  : "text-muted"
                              }`}
                              style={{ cursor: "pointer", minWidth: "120px" }}
                              onClick={() =>
                                stepNumber < currentStep &&
                                setCurrentStep(stepNumber)
                              }
                            >
                              <div
                                className={`rounded-circle d-flex align-items-center justify-content-center mb-2 ${
                                  isActive
                                    ? "bg-primary text-white"
                                    : isCompleted
                                    ? "bg-success text-white"
                                    : "bg-light text-muted"
                                }`}
                                style={{ width: "50px", height: "50px" }}
                              >
                                <StepIcon size={24} />
                              </div>
                              <small className="text-center">
                                {step.title}
                              </small>
                            </div>
                          );
                        })}
                      </div>

                      {/* Desktop Form Content */}
                      <div className="">
                        <div className="agent-wizard-form-section">
                          {/* Render step content */}
                          {renderStepContent()}
                        </div>

                        {/* Navigation Footer */}
                        <div className="d-flex justify-content-between align-items-center mt-4">
                          <Button
                            variant="outline-secondary"
                            type="button"
                            onClick={prevStep}
                            disabled={currentStep === 1}
                          >
                            <IconChevronLeft className="me-1" size={16} />
                            Anterior
                          </Button>

                          <span className="text-muted">
                            Etapa {currentStep} de 2
                          </span>

                          {currentStep < 2 ? (
                            <Button
                              variant="primary"
                              type="button"
                              onClick={nextStep}
                            >
                              Próximo
                              <IconChevronRight className="ms-1" size={16} />
                            </Button>
                          ) : (
                            <Button
                              variant="success"
                              type="button"
                              disabled={loading}
                              onClick={handleCreateAgent}
                            >
                              {loading ? (
                                <Spinner size="sm" className="me-1" />
                              ) : (
                                <IconDeviceFloppy className="me-1" size={16} />
                              )}
                              {isEditing ? "Salvar Alterações" : "Criar Agente"}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>
          </Form>
        </div>

        {/* Mobile Bottom Navigation */}
        <div className="agent-wizard-bottom-nav d-md-none">
          <div className="agent-wizard-nav-content">
            <Button
              className="agent-wizard-prev-btn"
              type="button"
              onClick={prevStep}
              disabled={currentStep === 1}
            >
              <IconChevronLeft size={18} />
              Anterior
            </Button>

            {currentStep < 2 ? (
              <Button
                className="agent-wizard-next-btn"
                type="button"
                onClick={nextStep}
              >
                Próximo
                <IconChevronRight size={18} />
              </Button>
            ) : (
              <Button
                className="agent-wizard-submit-btn"
                type="button"
                disabled={loading}
                onClick={handleCreateAgent}
              >
                {loading ? (
                  <Spinner size="sm" className="me-2" />
                ) : (
                  <IconDeviceFloppy size={18} />
                )}
                {isEditing ? "Salvar" : "Criar Agente"}
              </Button>
            )}
          </div>
        </div>
      </>
    );
  }
);

SimpleAgentWizard.displayName = "SimpleAgentWizard";

export default SimpleAgentWizard;
